/**
 * Centralized logger.
 *
 * - In development (`import.meta.env.DEV === true`), all levels print to the
 *   browser console with a small prefix so it's easy to spot Forja-originated
 *   logs amongst third-party noise.
 * - In production, only `warn` and `error` are forwarded to the console; `log`,
 *   `info` and `debug` are silently dropped to keep the console clean for end
 *   users and to avoid leaking internal details.
 *
 * Usage:
 *
 *   import { logger } from '@/lib/logger';
 *   logger.log('cache hit', key);
 *   logger.warn('retrying upload', { attempt });
 *   logger.error('failed to load profile', err);
 *
 * Migrate `console.log/info/debug` → `logger.log/info/debug` opportunistically.
 * Keep `console.error` for genuinely user-impacting failures or migrate to
 * `logger.error` (same behavior in production, prettier in dev).
 */

const isDev = typeof import.meta !== 'undefined' && Boolean(import.meta.env?.DEV);

const PREFIX = '[forja]';

function noop(): void {
  /* no-op in production */
}

export const logger = {
  log: isDev ? (...args: unknown[]) => console.log(PREFIX, ...args) : noop,
  info: isDev ? (...args: unknown[]) => console.info(PREFIX, ...args) : noop,
  debug: isDev ? (...args: unknown[]) => console.debug(PREFIX, ...args) : noop,
  // Warnings and errors always surface — they indicate real problems users may
  // need to see in production support sessions.
  warn: (...args: unknown[]) => console.warn(PREFIX, ...args),
  error: (...args: unknown[]) => console.error(PREFIX, ...args),
};

export type Logger = typeof logger;